import { createModuleBuilder } from '@evidentid/dashboard-commons/app';
import { createPersistingErrorModule } from '@evidentid/dashboard-commons/modules/persisting-error';
import { createSnackbarModule } from '@evidentid/dashboard-commons/modules/snackbar';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { RbacRole } from '@evidentid/tprm-portal-lib/models/rbac';
import { UserManagementApiClient } from '@evidentid/user-management-api-client';
import type { User } from '@evidentid/user-management-api-client/types';

import DeleteUserProcedure from './procedures/DeleteUserProcedure.vue';
import EditUserProcedure from './procedures/EditUserProcedure.vue';
import InviteUserProcedure from './procedures/InviteUserProcedure.vue';
import ResendInviteProcedure from './procedures/ResendInviteProcedure.vue';
import RoleChangeConfirmationProcedure from './procedures/role-change-confirmation-procedure/RoleChangeConfirmationProcedure.vue';
import { UserManagementConfig, UserManagementProcedure } from './types';
import UsersDashboard from './views/UsersDashboard.vue';

// TODO: add logging
export function createUserManagementModule() {
    return createModuleBuilder()
        .demandOptions<{
        userManagementConfig: UserManagementConfig;
        userManagementApiUrl: string;
    }>()
        .registerRoutes((app) => [
            {
                path: '/:rpId?/admin/users',
                name: 'manage-users',
                component: UsersDashboard as any,
                meta: { title: 'Users & Roles', pageClass: 'UsersDashboard' },
                ...(app.options.userManagementConfig.routeAlias?.['manage-users']
                    && { alias: app.options.userManagementConfig.routeAlias['manage-users'] }
                ),
            },
        ])
        .demandContext<{
        auth: { getTokens(): Promise<{ accessToken: string | null, idToken: string | null }> };
        gatewayClient: GatewayApiClient;
    }>()
        .inject(async (app) => {
            const userManagement = new UserManagementApiClient(app.options.userManagementApiUrl);
            userManagement.setTokens(() => app.auth.getTokens());
            return { userManagement };
        })
        .injectVue((app) => ({
            $userManagement: {
                config: app.options.userManagementConfig,
            },
        }))
        .demand<ReturnType<typeof createPersistingErrorModule>>()
        .demand<ReturnType<typeof createSnackbarModule>>()
        .registerProcedures<{
        [UserManagementProcedure.invite]: { rpName: string, rpDisplayName?: string | null };
        [UserManagementProcedure.edit]: { rpName: string, user: User };
        [UserManagementProcedure.delete]: { rpName: string, user: User };
        [UserManagementProcedure.resendInvite]: { rpName: string, user: User };
        [UserManagementProcedure.roleChangeConfirmation]: { rpName: string, user: User, newRole: RbacRole };
    }>({
            [UserManagementProcedure.invite]: InviteUserProcedure,
            [UserManagementProcedure.edit]: EditUserProcedure,
            [UserManagementProcedure.delete]: DeleteUserProcedure,
            [UserManagementProcedure.resendInvite]: ResendInviteProcedure,
            [UserManagementProcedure.roleChangeConfirmation]: RoleChangeConfirmationProcedure,
        })
        .end();
}

export type UserManagementModule = ReturnType<typeof createUserManagementModule>;
